import { useContext } from 'react';

import {
  Button,
  Icons,
  Input,
  LabelChipV2,
  Skeleton,
  Stack,
  Tooltip,
  IconButton,
  Typography,
  Box,
  tokens,
} from '@cast/design-system';
import {
  WorkloadDetailsContext,
  canEditWorkload,
  usePermitEditWorkload,
} from '@cast/split-me/features/cluster/workload-optimization';
import {
  useIsWoopDemoMode,
  type PreparedWorkload,
} from '@cast/split-me/features/workload-optimization';

import { useInventoryWorkloadOptimization } from '../_hooks/useInventoryWorkloadOptimization';

const FollowPolicyIcon = () => (
  <Box
    sx={{
      transform: 'rotate(45deg)',
      width: 12,
      height: 12,
      backgroundColor: tokens.theme.bg.structural.surfaceRaised,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Icons.Link
      size={8}
      weight="bold"
      color={tokens.theme.fg.structural.primary}
    />
  </Box>
);

type OptimizationContentProps = {
  workload: PreparedWorkload;
};

const OptimizationContent = ({ workload }: OptimizationContentProps) => {
  const { isOverridden, onOverride, onRevert } =
    useInventoryWorkloadOptimization({
      workload,
    });
  const { isNamespaceWritable } = useContext(WorkloadDetailsContext);
  const { permitEdit } = usePermitEditWorkload();
  const isWoopDemoMode = useIsWoopDemoMode();

  const { isVerticalScalingEnabled, isHorizontalScalingEnabled } = workload;
  const canOverrideOrRevert = isNamespaceWritable(workload.namespace);
  const canEditWorkloadField =
    permitEdit && !isWoopDemoMode && canEditWorkload(workload);

  return (
    <Stack gap={4}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
      >
        <Typography variant="P12M" color={tokens.theme.fg.structural.secondary}>
          Workload Optimization
        </Typography>

        {!workload.isManagedByAnnotations && canOverrideOrRevert && (
          <Stack direction="row" alignItems="center" gap={8}>
            <Button
              variant="text"
              endIcon={<Icons.CastHorizontalLinkDash size={16} />}
              onClick={onOverride}
              disabled={!canEditWorkloadField}
              testId="workload-optimization-override"
              aria-label="Override"
            />
            {isOverridden && (
              <Button
                variant="text"
                endIcon={
                  <Icons.ArrowCounterClockwise size={16} weight="regular" />
                }
                onClick={onRevert}
                disabled={!canEditWorkloadField}
                testId="workload-optimization-revert"
                aria-label="Revert to policy"
              />
            )}
          </Stack>
        )}
      </Stack>
      <Input
        readOnly
        size="medium"
        startAdornment={
          <Stack direction="row" alignItems="center" gap={4}>
            <LabelChipV2
              label="V"
              size="xs"
              color={isVerticalScalingEnabled ? 'blue' : 'grey'}
            />
            <LabelChipV2
              label="H"
              size="xs"
              color={isHorizontalScalingEnabled ? 'blue' : 'grey'}
            />
            {isOverridden ? (
              <LabelChipV2
                label="Overrides policy"
                size="xs"
                color="grey"
                slots={{
                  startIcon: <Icons.CastHorizontalLinkDash size={12} />,
                }}
              />
            ) : (
              <LabelChipV2
                label="Follows policy"
                size="xs"
                color="grey"
                slots={{
                  startIcon: <FollowPolicyIcon />,
                }}
              />
            )}
          </Stack>
        }
        endAdornment={
          <Tooltip title="Optimization options">
            <IconButton
              variant="ghost"
              size="sm"
              disabled
              aria-label="Optimization options"
              testId="workload-optimization-options"
            >
              <Icons.CaretDown />
            </IconButton>
          </Tooltip>
        }
        testId="workload-optimization-select"
      />
    </Stack>
  );
};

export const InventoryWorkloadOptimizationStatus = () => {
  const { workload } = useContext(WorkloadDetailsContext);

  if (!workload) {
    return (
      <Stack gap={4} width="100%">
        <Skeleton height={20} width="30%" />
        <Skeleton height={40} width="100%" />
      </Stack>
    );
  }

  return <OptimizationContent workload={workload} />;
};
